import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Share,
  Plus,
  MoreVertical,
  Home,
  Smartphone,
  Monitor,
  CheckCircle2,
  Bell,
  Wifi,
  Lock,
} from 'lucide-react';

/* /install — full educational page (Phase A round 1).
 *
 * This page exists for visitors who:
 *   • Dismissed the in-flow install banner and want to revisit
 *   • Came here from a "Learn more" link in the banner or walkthrough
 *   • Want platform-specific steps (parents browsing from a desktop, kids on iOS)
 *   • Are skeptical and need the "why" before they commit
 *
 * Voice: Coach Lewis — direct, no hype, value-first.
 */

const StepCard = ({ index, icon, title, body, testId }) => (
  <li
    className="flex gap-3 sm:gap-4 items-start"
    data-testid={testId}
  >
    <div className="shrink-0 h-10 w-10 rounded-xl bg-[#004C54]/10 border border-[#004C54]/20 inline-flex items-center justify-center">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[14px] font-semibold text-foreground leading-snug">
        {index}. {title}
      </div>
      <p className="text-[13px] text-foreground/65 leading-snug mt-1">{body}</p>
    </div>
  </li>
);

const WhyCard = ({ icon, title, body }) => (
  <div className="rounded-xl border border-black/8 bg-white p-4 sm:p-5">
    <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#004C54]/10 text-[#004C54] mb-3">
      {icon}
    </div>
    <div className="font-heading text-[14px] font-bold text-foreground">{title}</div>
    <p className="text-[12.5px] text-foreground/65 leading-snug mt-1.5">{body}</p>
  </div>
);

export const InstallGuide = () => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.title = 'Put Apex Mind on your phone | Apex Athletic Mind';
    }
  }, []);

  return (
    <main
      className="min-h-screen bg-[#FAFAF8] text-[#1a1a1a]"
      data-testid="install-page"
    >
      {/* Top bar */}
      <div className="border-b border-black/8 bg-white">
        <div className="max-w-[860px] mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] text-[#3a3a3a] hover:text-[#004C54] transition-colors"
            data-testid="install-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Apex Athletic Mind
          </Link>
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#004C54] font-semibold">
            Install Guide
          </span>
        </div>
      </div>

      <div className="max-w-[860px] mx-auto px-5 sm:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-12">
          <div className="font-mono-accent text-[11px] uppercase tracking-[0.22em] text-[#004C54] font-bold mb-3">
            10-Second Setup · No App Store Required
          </div>
          <h1 className="font-heading text-[28px] sm:text-[36px] font-bold text-foreground leading-tight">
            Put Coach Lewis on your home screen.
          </h1>
          <p className="text-[14.5px] sm:text-[16px] text-foreground/65 mt-3 max-w-[640px] mx-auto leading-snug">
            Apex Mind installs straight from this website. No App Store. No Play Store. One tap from your phone’s home screen and you’re in the Apex AI Coach — pre-game cues, mindset reps, and quick check-ins, all without opening a browser.
          </p>
        </div>

        {/* Why install */}
        <section className="mb-12 sm:mb-14">
          <div className="text-center mb-6">
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#004C54] font-bold mb-2">
              Why install
            </div>
            <h2 className="font-heading text-[22px] sm:text-[26px] font-bold text-foreground">
              Three reasons it’s worth ten seconds.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <WhyCard
              icon={<Bell className="h-4 w-4" />}
              title="Pre-game reminders"
              body="Opt in to get a Coach Lewis cue 2 hours before competition. Brief. Useful. No noise."
            />
            <WhyCard
              icon={<Wifi className="h-4 w-4" />}
              title="Works offline"
              body="Locker rooms, gym basements, away games. Your last session is cached so the app still opens."
            />
            <WhyCard
              icon={<Lock className="h-4 w-4" />}
              title="One tap, always there"
              body="No bookmark, no Safari, no Google sign-in fumble. Tap the Apex icon, you’re in."
            />
          </div>
        </section>

        {/* iOS */}
        <section className="mb-12 sm:mb-14" data-testid="install-section-ios">
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#004C54] text-white">
              <Smartphone className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-heading text-[20px] sm:text-[24px] font-bold text-foreground leading-tight">
                On iPhone (Safari)
              </h2>
              <p className="text-[12.5px] text-foreground/55">Works on iPhone 8 and newer · iOS 14+</p>
            </div>
          </div>
          <div className="rounded-2xl border border-black/8 bg-white p-5 sm:p-6">
            <ol className="space-y-4">
              <StepCard
                index="1"
                icon={<Share className="h-4 w-4 text-[#004C54]" />}
                title="Open this site in Safari, tap the Share icon"
                body="It’s the square with an upward arrow, usually at the bottom of the screen. (Chrome on iPhone doesn’t support installs — use Safari.)"
                testId="install-step-ios-1"
              />
              <StepCard
                index="2"
                icon={<Plus className="h-4 w-4 text-[#004C54]" />}
                title='Scroll and pick "Add to Home Screen"'
                body="A new sheet appears with the Apex Mind icon and a name field. You can rename it if you want — we like ‘Coach Lewis.’"
                testId="install-step-ios-2"
              />
              <StepCard
                index="3"
                icon={<Home className="h-4 w-4 text-[#004C54]" />}
                title='Tap "Add" in the top right'
                body="Done. Look at your home screen. Apex Mind is there. Tap it — it opens full-screen, no Safari address bar, no tabs."
                testId="install-step-ios-3"
              />
            </ol>
          </div>
        </section>

        {/* Android */}
        <section className="mb-12 sm:mb-14" data-testid="install-section-android">
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#004C54] text-white">
              <Smartphone className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-heading text-[20px] sm:text-[24px] font-bold text-foreground leading-tight">
                On Android (Chrome or Edge)
              </h2>
              <p className="text-[12.5px] text-foreground/55">Most Samsung, Pixel, Motorola, OnePlus · Android 8+</p>
            </div>
          </div>
          <div className="rounded-2xl border border-black/8 bg-white p-5 sm:p-6">
            <ol className="space-y-4">
              <StepCard
                index="1"
                icon={<MoreVertical className="h-4 w-4 text-[#004C54]" />}
                title="Tap the 3-dot menu in Chrome"
                body="Top-right of the browser. (On Samsung’s Internet browser, look for the same menu.)"
                testId="install-step-android-1"
              />
              <StepCard
                index="2"
                icon={<Plus className="h-4 w-4 text-[#004C54]" />}
                title='Pick "Install app" or "Add to Home screen"'
                body="The exact wording depends on your version of Android. The result is the same."
                testId="install-step-android-2"
              />
              <StepCard
                index="3"
                icon={<Home className="h-4 w-4 text-[#004C54]" />}
                title="Confirm. You’re done."
                body="Apex Mind appears on your home screen. Tap it to open in app mode — no browser chrome, faster launch, optional push notifications."
                testId="install-step-android-3"
              />
            </ol>
          </div>
        </section>

        {/* Desktop */}
        <section className="mb-12 sm:mb-14" data-testid="install-section-desktop">
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#004C54] text-white">
              <Monitor className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-heading text-[20px] sm:text-[24px] font-bold text-foreground leading-tight">
                On Mac or PC (Chrome / Edge)
              </h2>
              <p className="text-[12.5px] text-foreground/55">Optional — for coaches and parents who want a desktop shortcut</p>
            </div>
          </div>
          <div className="rounded-2xl border border-black/8 bg-white p-5 sm:p-6">
            <ol className="space-y-4">
              <StepCard
                index="1"
                icon={<Plus className="h-4 w-4 text-[#004C54]" />}
                title="Look at the right side of the address bar"
                body='When the page loads, you should see a small install icon (a monitor with a downward arrow) or a tooltip that says "Install Apex Mind."'
                testId="install-step-desktop-1"
              />
              <StepCard
                index="2"
                icon={<CheckCircle2 className="h-4 w-4 text-[#004C54]" />}
                title='Click it, then click "Install"'
                body="A small dialog confirms. Apex Mind opens in its own window, like a desktop app."
                testId="install-step-desktop-2"
              />
              <StepCard
                index="3"
                icon={<Home className="h-4 w-4 text-[#004C54]" />}
                title="It now lives in your dock / taskbar"
                body="Same login as the website. Launch it any time without opening a browser tab."
                testId="install-step-desktop-3"
              />
            </ol>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12 sm:mb-14" data-testid="install-faq">
          <div className="text-center mb-6">
            <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#004C54] font-bold mb-2">
              Straight Answers
            </div>
            <h2 className="font-heading text-[22px] sm:text-[26px] font-bold text-foreground">
              What parents actually ask.
            </h2>
          </div>
          <div className="space-y-3">
            {[
              {
                q: 'Is this a real app from the App Store?',
                a: 'No. It\u2019s a Progressive Web App (PWA) \u2014 it lives on your home screen like an app, but it installs directly from this website. No App Store account, no download, no review. We may publish a native App Store version later; for now this is the fastest path to value.',
              },
              {
                q: 'Will it spam me with notifications?',
                a: 'Only if you opt in, and only with stuff Coach Lewis would actually say. Examples: pre-game cues 2 hours before competition, a Monday mindset rep, a journal nudge after a tough loss. You can turn notifications off any time in your phone settings.',
              },
              {
                q: 'Does it work without internet?',
                a: 'Yes, partially. Pages you\u2019ve already visited stay cached and open instantly even with no signal. Live coaching features (the AI Coach, fresh content) need a connection.',
              },
              {
                q: 'Will it use up storage on my phone?',
                a: 'Almost none. Apex Mind is a few megabytes \u2014 about 1/100th the size of an Instagram update.',
              },
              {
                q: 'How do I uninstall?',
                a: 'Same way as any app icon. Long-press the Apex Mind icon, tap Remove or Delete, confirm. Your account stays \u2014 you can re-install any time.',
              },
              {
                q: 'Does my login carry over?',
                a: 'Yes. Same email and password as the website. Open the installed app and you\u2019re already signed in.',
              },
            ].map((item, idx) => (
              <details
                key={`faq-${idx}`}
                className="group bg-white rounded-xl border border-black/8 px-4 sm:px-5 py-4 open:shadow-sm transition-shadow"
                data-testid={`install-faq-${idx + 1}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3">
                  <span className="font-heading text-[14px] sm:text-[15px] font-bold text-foreground leading-snug">
                    {item.q}
                  </span>
                  <span className="text-[#004C54] text-[18px] leading-none mt-0.5 transition-transform group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="text-[13px] sm:text-[13.5px] text-foreground/70 leading-snug mt-3">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="rounded-2xl bg-[#0B0C0D] text-white p-6 sm:p-8 text-center" data-testid="install-final-cta">
          <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#4FBCC7] font-bold mb-2">
            One more thing
          </div>
          <h2 className="font-heading text-[20px] sm:text-[24px] font-bold leading-tight">
            Open this page on your phone if you haven’t already.
          </h2>
          <p className="text-[13px] text-white/65 mt-2 max-w-[520px] mx-auto leading-snug">
            The install steps only work on your phone or tablet — the device you actually want Apex Mind on. Send yourself the link if you’re reading on a desktop.
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <a
              href="/"
              className="inline-flex items-center justify-center rounded-full bg-[#004C54] hover:bg-[#003a40] text-white text-[12.5px] font-semibold px-5 h-10 transition-colors"
              data-testid="install-final-cta-home"
            >
              Back to homepage
            </a>
            <a
              href="/app/login"
              className="inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/15 text-white text-[12.5px] font-semibold px-5 h-10 transition-colors"
              data-testid="install-final-cta-app"
            >
              Sign in to Apex Mind
            </a>
          </div>
        </section>
      </div>
    </main>
  );
};

export default InstallGuide;
